import React, { useState, useEffect } from 'react';
import {
   Card,
   CardContent,
   Typography,
   Avatar,
   Box,
   Chip,
   CircularProgress,
} from '@mui/material';
import {
   Event as EventIcon,
   Lock as LockIcon,
   Public as PublicIcon,
   CalendarToday as CalendarTodayIcon,
   LocationOn as LocationOnIcon,
   VideoCall as VideoCallIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

export interface EventCreated {
   id: string;
   card_type: 'event_created';
   userid: string;
   eventId: string;
   eventTitle: string;
   eventDescription?: string;
   eventCoverUrl?: string;
   eventStartDate: string;
   eventEndDate?: string;
   eventLocation?: string;
   eventType?: string;
   eventIsPublic: boolean;
   eventIsVirtual: boolean;
   creatorId: string;
   creatorName: string;
   creatorPhoto?: string;
   creatorEmail: string;
   createdAt: any;
   seen: boolean;
}

interface EventCreatedCardProps {
   eventCreated: EventCreated;
   onViewEvent: (eventId: string) => void;
   onMarkAsSeen?: (cardId: string) => void;
}

const EventCreatedCard: React.FC<EventCreatedCardProps> = ({
   eventCreated,
   onViewEvent,
   onMarkAsSeen,
}) => {
   const [eventData, setEventData] = useState<any>(null);
   const [loading, setLoading] = useState(true);

   useEffect(() => {
      const fetchEventData = async () => {
         try {
            const eventDoc = await getDoc(doc(db, 'events', eventCreated.eventId));
            if (eventDoc.exists()) {
               setEventData(eventDoc.data());
            }
         } catch (error) {
            console.error('Error fetching event data:', error);
         } finally {
            setLoading(false);
         }
      };

      fetchEventData();
   }, [eventCreated.eventId]);

   const handleClick = () => {
      if (onMarkAsSeen) {
         onMarkAsSeen(eventCreated.id);
      }
      onViewEvent(eventCreated.eventId);
   };

   const getTimeAgo = () => {
      if (!eventCreated.createdAt) return '';
      try {
         const date =
            eventCreated.createdAt.toDate?.() || new Date(eventCreated.createdAt.seconds * 1000);
         return formatDistanceToNow(date, { addSuffix: true });
      } catch {
         return '';
      }
   };

   const formatEventDate = (date: any) => {
      try {
         let dateObj;
         if (date?.toDate) {
            dateObj = date.toDate();
         } else if (date?.seconds) {
            dateObj = new Date(date.seconds * 1000);
         } else if (typeof date === 'string') {
            dateObj = new Date(date);
         } else {
            dateObj = date;
         }
         
         return dateObj.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
         });
      } catch {
         return '';
      }
   };

   const getLocationString = (location: any) => {
      if (!location) return '';
      if (typeof location === 'string') return location;
      if (typeof location === 'object') {
         return location.name || location.address || '';
      }
      return '';
   };

   if (loading) {
      return (
         <Card
            sx={{
               mb: 1.5,
               border: '1px solid',
               borderColor: 'divider',
               borderRadius: 2,
               p: 2,
            }}
         >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 56 }}>
               <CircularProgress size={24} sx={{ color: '#00C4A7' }} />
            </Box>
         </Card>
      );
   }

   if (!eventData) {
      return null; // Event was deleted
   }

   const coverUrl = eventData.coverImageUrl || eventData.coverPhotoUrl || eventCreated.eventCoverUrl;
   const isPublic = eventData.isPublic ?? eventCreated.eventIsPublic;
   const isVirtual = eventData.isVirtual ?? eventCreated.eventIsVirtual;
   const startDate = eventData.startDate || eventCreated.eventStartDate;
   const location = eventData.location || eventCreated.eventLocation;

   return (
      <Card
         sx={{
            mb: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: '3px solid #22C55E',
            borderRadius: 2,
            transition: 'all 0.2s',
            cursor: 'pointer',
            '&:hover': {
               boxShadow: 3,
               transform: 'translateY(-2px)',
               borderColor: '#00C4A7',
            },
         }}
         onClick={handleClick}
      >
         <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
               {/* Event Cover Photo */}
               {coverUrl ? (
                  <Box
                     component="img"
                     src={coverUrl}
                     alt={eventData.title || eventCreated.eventTitle}
                     sx={{
                        width: 56,
                        height: 56,
                        objectFit: 'cover',
                        borderRadius: 2,
                     }}
                  />
               ) : (
                  <Avatar
                     sx={{
                        width: 56,
                        height: 56,
                        bgcolor: '#00C4A7',
                        borderRadius: 2,
                     }}
                  >
                     <EventIcon sx={{ fontSize: 32, color: 'white' }} />
                  </Avatar>
               )}

                  {/* Main Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#00C4A7' }}>
                           New Event Created!
                        </Typography>
                        <Chip
                           icon={
                              isPublic ? (
                                 <PublicIcon sx={{ fontSize: 14 }} />
                              ) : (
                                 <LockIcon sx={{ fontSize: 14 }} />
                              )
                           }
                           label={isPublic ? 'Public' : 'Private'}
                           size="small"
                           sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              bgcolor: isPublic ? '#10B981' : '#6366f1',
                              color: 'white',
                              '& .MuiChip-icon': {
                                 color: 'white',
                              },
                           }}
                        />
                        {isVirtual && (
                           <Chip
                              icon={<VideoCallIcon sx={{ fontSize: 14 }} />}
                              label="Virtual"
                              size="small"
                              sx={{
                                 height: 20,
                                 fontSize: '0.7rem',
                                 bgcolor: '#f59e0b',
                                 color: 'white',
                                 '& .MuiChip-icon': {
                                    color: 'white',
                                 },
                              }}
                           />
                        )}
                     </Box>

                     <Typography
                        variant="body1"
                        sx={{
                           fontWeight: 600,
                           mb: 0.5,
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           display: '-webkit-box',
                           WebkitLineClamp: 1,
                           WebkitBoxOrient: 'vertical',
                        }}
                     >
                        {eventData.title || eventCreated.eventTitle}
                     </Typography>

                     {(eventData.description || eventCreated.eventDescription) && (
                        <Typography
                           variant="body2"
                           color="text.secondary"
                           sx={{
                              mb: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                           }}
                        >
                           {eventData.description || eventCreated.eventDescription}
                        </Typography>
                     )}

                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                           <Typography variant="caption" color="text.secondary">
                              {formatEventDate(startDate)}
                           </Typography>
                        </Box>

                        {getLocationString(location) && (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography
                                 variant="caption"
                                 color="text.secondary"
                                 sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 150,
                                 }}
                              >
                                 {getLocationString(location)}
                              </Typography>
                           </Box>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                           {getTimeAgo()}
                        </Typography>
                     </Box>
                  </Box>
               </Box>
            </CardContent>
         </Card>
   );
};

export default EventCreatedCard;

